// services/NotificationService.ts
import type { CSSProperties } from 'react'

export type NotificationSeverity = 'success' | 'error' | 'warning' | 'info'

export interface NotificationOptions {
  duration?: number
  position?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }
  /** Style snackbar (vd. maxWidth hẹp để toast thông báo xuống nhiều dòng) */
  style?: CSSProperties
  preventDuplicate?: boolean
  persist?: boolean
  action?: {
    label: string
    onClick: () => void
  }
}

// Type for the notification handler function
export type NotificationHandler = (
  message: string,
  severity?: NotificationSeverity,
  options?: NotificationOptions,
) => void

class NotificationService {
  private handler: NotificationHandler | null = null

  /**
   * Set the global notification handler
   * This should be called once from your app root after MUI setup
   */
  setHandler(handler: NotificationHandler): void {
    this.handler = handler
  }

  /**
   * Show a notification
   */
  show(message: string, severity: NotificationSeverity = 'info', options?: NotificationOptions): void {
    try {
      if (this.handler) {
        this.handler(message, severity, options)
      } else {
        // Fallback methods when no handler is set
        this.fallbackNotification(message, severity)
      }
    } catch (error) {
      console.warn('Notification failed:', error)
      this.fallbackNotification(message, severity)
    }
  }

  /**
   * Convenience methods for different severity levels
   */
  success(message: string, options?: NotificationOptions): void {
    this.show(message, 'success', options)
  }

  error(message: string, options?: NotificationOptions): void {
    this.show(message, 'error', options)
  }

  warning(message: string, options?: NotificationOptions): void {
    this.show(message, 'warning', options)
  }

  info(message: string, options?: NotificationOptions): void {
    this.show(message, 'info', options)
  }

  /**
   * Check if notification handler is available
   */
  isAvailable(): boolean {
    return this.handler !== null
  }

  /**
   * Clear the notification handler
   */
  clearHandler(): void {
    this.handler = null
  }

  /**
   * Fallback notification methods
   */
  private fallbackNotification(message: string, severity: NotificationSeverity): void {
    if (typeof window !== 'undefined') {
      // Try legacy showToast if available
      if ((window as any).showToast) {
        ;(window as any).showToast(message, severity)
        return
      }

      // Browser notification for critical messages
      if ((severity === 'error' || severity === 'warning') && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification(`${severity.toUpperCase()}: ${message}`)
          return
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
              new Notification(`${severity.toUpperCase()}: ${message}`)
            }
          })
          return
        }
      }

      // Console logging with styled output
      const styles = {
        success: 'color: #4caf50; font-weight: bold;',
        error: 'color: #f44336; font-weight: bold;',
        warning: 'color: #ff9800; font-weight: bold;',
        info: 'color: #2196f3; font-weight: bold;',
      }

      console.log(`%c🔔 [${severity.toUpperCase()}] ${message}`, styles[severity] || styles.info)
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService()

// Export default for convenience
export default notificationService
